// Copyright (c) 2019-2024 Cigent Technology Inc. All Rights Reserved.

import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {ID3EPolicy} from '../../../../types/d3ePolicy';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {
  BitLockerEncryption, BitlockerPolicySection,
  EncryptionMethod,
  EncryptionType,
  RecoveryKeyFileCreation,
  RecoveryPackage,
  RecoveryPasswordFileCreation,
  StartupAuthType
} from '../../../../types/subscription';

const getValue = (value: any, defaultValue: any): any => {
  if (value == undefined) {
    return defaultValue;
  }
  return value;
};

@Component({
    selector: 'app-bitlocker',
    templateUrl: './bitlocker.component.html',
    styleUrls: ['./bitlocker.component.scss'],
    standalone: false
})
export class BitlockerComponent implements OnChanges {
  @Input() d3ePolicy: ID3EPolicy;
  @Output() onChanged:  EventEmitter<void> = new EventEmitter<void>();

  protected readonly BitLockerEncryption = BitLockerEncryption;

  // Expose enums / interfaces
  public RecoveryPackage = RecoveryPackage;
  public RecoveryKeyFileCreation = RecoveryKeyFileCreation;
  public RecoveryPasswordFileCreation = RecoveryPasswordFileCreation;
  public EncryptionType = EncryptionType;
  public EncryptionMethod = EncryptionMethod;
  public StartupAuthType = StartupAuthType;

  osDriveMinimumPINLength: number = 6;

  formGroup: UntypedFormGroup;

  numericInputLabelStyles = {
    'margin': '0'
  };

  constructor(private readonly formBuilder: UntypedFormBuilder) {}

  ngOnChanges(changes: SimpleChanges) {
    const firstChange = changes['d3ePolicy']?.firstChange;
    const hasChanged = changes['d3ePolicy']?.currentValue !== changes['d3ePolicy']?.previousValue;
    if (firstChange || hasChanged) {
      const startupAuthType = parseInt(getValue(this.d3ePolicy.bitlocker?.osDrive?.startupAuthType, StartupAuthType.tpmOnly))
      this.formGroup = this.formBuilder.group({
          // General Settings
          backupKeyToActiveDirectory: [getValue(this.d3ePolicy.bitlocker?.generalSettings?.backupKeyToActiveDirectory, false)],
          backupKeyToManagementConsole: [getValue(this.d3ePolicy.bitlocker?.generalSettings?.backupKeyToManagementConsole, false)],

          // osDrive
          osDriveConfigurePreBootRecoveryMessage: [getValue(this.d3ePolicy.bitlocker?.osDrive?.configurePreBootRecoveryMessage, false)],
          osDriveAllowBitlockerWithoutCompatibleTPM: [getValue(this.d3ePolicy.bitlocker?.osDrive?.allowBitlockerWithoutCompatibleTPM, false)],
          osDriveRequireStartupAuth: [getValue(this.d3ePolicy.bitlocker?.osDrive?.requireStartupAuth, false)],
          osDriveManageBitlockerEncryption: [getValue(this.d3ePolicy.bitlocker?.osDrive?.manageBitlockerEncryption, BitLockerEncryption.doNotManage)],
          osDriveAllowEnhancedPINSForStartup: [getValue(this.d3ePolicy.bitlocker?.osDrive?.allowEnhancedPINSForStartup, false)],
          osDriveDisallowStandardUsersChangingAuth: [getValue(this.d3ePolicy.bitlocker?.osDrive?.disallowStandardUsersChangingAuth, false)],
          osDriveEncryptionMethod: [getValue(this.d3ePolicy.bitlocker?.osDrive?.encryptionMethod, EncryptionMethod.xtsAes128)],
          osDriveEncryptionType: [getValue(this.d3ePolicy.bitlocker?.osDrive?.encryptionType, EncryptionType.usedSpaceOnly)],
          osDrivePreBootRecoveryMessage: [getValue(this.d3ePolicy.bitlocker?.osDrive?.preBootRecoveryMessage, '')],
          osDrivePreBootRecoveryURL: [getValue(this.d3ePolicy.bitlocker?.osDrive?.preBootRecoveryURL, '')],
          osDriveStartupAuthType: [startupAuthType],
          osDriveRecoveryPackage: [getValue(this.d3ePolicy.bitlocker?.osDrive?.recoveryPackage, RecoveryPackage.passwordOnly)],
          osDriveRecoveryKeyFileCreation: [getValue(this.d3ePolicy.bitlocker?.osDrive?.recoveryKeyFileCreation, RecoveryKeyFileCreation.allowed)],
          osDriveRecoveryPasswordFileCreation: [getValue(this.d3ePolicy.bitlocker?.osDrive?.recoveryPasswordFileCreation, RecoveryPasswordFileCreation.allowed)],

          // dataDrives
          dataDriveAutoUnlock: true,
          dataDrivesRecoveryPackage: [getValue(this.d3ePolicy.bitlocker?.dataDrives?.recoveryPackage, RecoveryPackage.passwordOnly)],
          dataDrivesRecoveryKeyFileCreation: [getValue(this.d3ePolicy.bitlocker?.dataDrives?.recoveryKeyFileCreation, RecoveryKeyFileCreation.allowed)],
          dataDrivesRecoveryPasswordFileCreation: [getValue(this.d3ePolicy.bitlocker?.dataDrives?.recoveryPasswordFileCreation, RecoveryPasswordFileCreation.allowed)],
          dataDrivesEncryptionType: [getValue(this.d3ePolicy.bitlocker?.dataDrives?.encryptionType, EncryptionType.usedSpaceOnly)],
          dataDrivesEncryptionMethod: [getValue(this.d3ePolicy.bitlocker?.dataDrives?.encryptionMethod, EncryptionMethod.xtsAes128)],
          dataDrivesManageBitlockerEncryption: [getValue(this.d3ePolicy.bitlocker?.dataDrives?.manageBitlockerEncryption, BitLockerEncryption.doNotManage)],
      });

      this.osDriveMinimumPINLength = getValue(this.d3ePolicy.bitlocker?.osDrive?.minimumPINLength, 6);
      this.setEnabledDisabled();
    }


  }

  osDriveMinPINChanged(value) {
    if (this.osDriveMinimumPINLength != value) {
      this.osDriveMinimumPINLength = value;
      this.onDataChanged();
    }
  }

  onDataChanged() {
    // Getting raw value because when the control is disabled it has a value of undefined
    const {osDriveStartupAuthType} = this.formGroup.getRawValue();
    this.d3ePolicy.bitlocker = {
      generalSettings: {
        backupKeyToActiveDirectory: this.formGroup.value.backupKeyToActiveDirectory,
        backupKeyToManagementConsole: this.formGroup.value.backupKeyToManagementConsole
      },
      osDrive: {
        configurePreBootRecoveryMessage: this.formGroup.value.osDriveConfigurePreBootRecoveryMessage,
        // allowBitlockerWithoutCompatibleTPM: this.formGroup.value.osDriveAllowBitlockerWithoutCompatibleTPM,
        allowBitlockerWithoutCompatibleTPM: false,
        requireStartupAuth: this.formGroup.value.osDriveRequireStartupAuth,
        manageBitlockerEncryption: this.formGroup.value.osDriveManageBitlockerEncryption,
        allowEnhancedPINSForStartup: this.formGroup.value.osDriveAllowEnhancedPINSForStartup,
        disallowStandardUsersChangingAuth: this.formGroup.value.osDriveDisallowStandardUsersChangingAuth,
        encryptionMethod: this.formGroup.value.osDriveEncryptionMethod,
        encryptionType: this.formGroup.value.osDriveEncryptionType,
        minimumPINLength: this.osDriveMinimumPINLength,
        preBootRecoveryMessage: this.formGroup.value.osDrivePreBootRecoveryMessage,
        preBootRecoveryURL: this.formGroup.value.osDrivePreBootRecoveryURL,
        startupAuthType: osDriveStartupAuthType,
        recoveryPackage: this.formGroup.value.osDriveRecoveryPackage,
        recoveryKeyFileCreation: this.formGroup.value.osDriveRecoveryKeyFileCreation,
        recoveryPasswordFileCreation: this.formGroup.value.osDriveRecoveryPasswordFileCreation
      },
      dataDrives: {
        autoUnlock: true,
        recoveryPackage: this.formGroup.value.dataDrivesRecoveryPackage,
        recoveryKeyFileCreation: this.formGroup.value.dataDrivesRecoveryKeyFileCreation,
        recoveryPasswordFileCreation: this.formGroup.value.dataDrivesRecoveryPasswordFileCreation,
        encryptionType: this.formGroup.value.dataDrivesEncryptionType,
        encryptionMethod: this.formGroup.value.dataDrivesEncryptionMethod,
        manageBitlockerEncryption: this.formGroup.value.dataDrivesManageBitlockerEncryption
      }
    } as BitlockerPolicySection;

    this.setEnabledDisabled();
    this.onChanged.emit();
  }

  setEnabledDisabled() {
    this.formGroup.get('dataDriveAutoUnlock').disable();

    if (this.formGroup.value.osDriveRequireStartupAuth) {
      this.formGroup.get("osDriveStartupAuthType").enable();
    } else {
      this.formGroup.get("osDriveStartupAuthType").disable();
    }
    if (this.formGroup.value.osDriveConfigurePreBootRecoveryMessage) {
      this.formGroup.get("osDrivePreBootRecoveryMessage").enable();
      this.formGroup.get("osDrivePreBootRecoveryURL").enable();
    } else {
      this.formGroup.get("osDrivePreBootRecoveryMessage").disable();
      this.formGroup.get("osDrivePreBootRecoveryURL").disable();
    }
    if (this.formGroup.value.backupKeyToActiveDirectory) {
      this.formGroup.get("osDriveRecoveryPackage").enable();
      this.formGroup.get("dataDrivesRecoveryPackage").enable();

      const osDriveRecoveryPackage = this.formGroup.get('osDriveRecoveryPackage').value;
      if (osDriveRecoveryPackage === RecoveryPackage.passwordOnly) {
        this.formGroup.get("osDriveRecoveryPasswordFileCreation").setValue(RecoveryPasswordFileCreation.allowed);
        this.formGroup.get("osDriveRecoveryKeyFileCreation").enable();
        this.formGroup.get("osDriveRecoveryPasswordFileCreation").disable();
      }
      if (osDriveRecoveryPackage === RecoveryPackage.passwordAndKey) {
        this.formGroup.get("osDriveRecoveryPasswordFileCreation").setValue(RecoveryPasswordFileCreation.allowed);
        this.formGroup.get("osDriveRecoveryKeyFileCreation").setValue(RecoveryKeyFileCreation.allowed);
        this.formGroup.get("osDriveRecoveryKeyFileCreation").disable();
        this.formGroup.get("osDriveRecoveryPasswordFileCreation").disable();
      }

      const dataDrivesRecoveryPackage = this.formGroup.get('dataDrivesRecoveryPackage').value;
      if (dataDrivesRecoveryPackage === RecoveryPackage.passwordOnly) {
        this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").setValue(RecoveryPasswordFileCreation.allowed);
        this.formGroup.get("dataDrivesRecoveryKeyFileCreation").enable();
        this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").disable();
      }
      if (dataDrivesRecoveryPackage === RecoveryPackage.passwordAndKey) {
        this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").setValue(RecoveryPasswordFileCreation.allowed);
        this.formGroup.get("dataDrivesRecoveryKeyFileCreation").setValue(RecoveryKeyFileCreation.allowed);
        this.formGroup.get("dataDrivesRecoveryKeyFileCreation").disable();
        this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").disable();
      }

    } else {
      this.formGroup.get("osDriveRecoveryPackage").disable();
      this.formGroup.get("dataDrivesRecoveryPackage").disable();
      this.formGroup.get("osDriveRecoveryKeyFileCreation").enable();
      this.formGroup.get("dataDrivesRecoveryKeyFileCreation").enable();
      this.formGroup.get("osDriveRecoveryPasswordFileCreation").enable();
      this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").enable();
    }
    if (this.formGroup.value.backupKeyToManagementConsole) {
      this.formGroup.get("osDriveRecoveryPasswordFileCreation").setValue(RecoveryPasswordFileCreation.allowed);
      this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").setValue(RecoveryPasswordFileCreation.allowed);
      this.formGroup.get("osDriveRecoveryPasswordFileCreation").disable();
      this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").disable();
    }
    if (!this.formGroup.value.backupKeyToManagementConsole && !this.formGroup.value.backupKeyToActiveDirectory) {
      this.formGroup.get("osDriveRecoveryPasswordFileCreation").enable();
      this.formGroup.get("dataDrivesRecoveryPasswordFileCreation").enable();
    }
  }
}
